import { t } from "@lingui/core/macro"
import { Sizes, Weights } from "../../../interfaces/sizing"
import { addPageAction } from "../../../utils/newrelic"
import { Typography } from "../../common"
import {
    ChevronLeftBlue,
    SearchPlaceholder,
} from "../../common/atoms/Icons/Chat"
import { Spinner } from "../../common/atoms/Spinner"
import { IconButton } from "../common/IconButton/IconButton"
import { Page } from "../common/Page/Page"
import { ProfilePicture } from "../common/ProfilePicture/ProfilePicture"
import { SearchInput } from "../common/SearchInput/SearchInput"
import { useMessagingView } from "../hooks"
import { classNames } from "../utils"
import styles from "./SearchUsers.module.scss"
import { GeneralViewModel } from "./viewModels/GeneralViewModel"
import { UnifiedViewModel } from "./viewModels/UnifiedViewModel"

const THROTTLE_DELAY_MS = 500

export function SearchUsers() {
    const messagingView = useMessagingView()

    const vmProps = {
        typingThrottle: THROTTLE_DELAY_MS,
    }
    const {
        searchResults,
        listRef,
        source,
        handleSearchChange,
        handleGoBack,
        handleUserSelect,
    } = messagingView.isDesktopUnified
        ? UnifiedViewModel(vmProps)
        : GeneralViewModel(vmProps)

    return (
        <Page className={styles.container} ref={listRef}>
            {messagingView.isDesktopUnified && (
                <div className={styles.unifiedDesktopHeader}>
                    <div className={styles.title}>{t`New Message`}</div>
                </div>
            )}
            <div
                className={classNames(styles.header, {
                    [styles.unifiedDesktop]: messagingView.isDesktopUnified,
                })}
            >
                {!messagingView.isDesktopUnified && (
                    <IconButton
                        icon={<ChevronLeftBlue height={16} width={10} />}
                        iconAlt="Back"
                        onClick={() => {
                            handleGoBack?.()
                            addPageAction("DmNewConversationUserSearch", {
                                actionType: "exit",
                                source: source,
                            })
                        }}
                        className={styles.backButton}
                    />
                )}
                <SearchInput
                    inputProps={{
                        value: searchResults.search,
                        onChange: handleSearchChange,
                        autoFocus: true,
                        maxLength: 30,
                        placeholder: t`Search creators`,
                    }}
                    onClear={
                        searchResults.search
                            ? () => {
                                  searchResults.setSearch("")
                                  addPageAction("DmNewConversationUserSearch", {
                                      actionType: "cleared",
                                      source: source,
                                  })
                              }
                            : undefined
                    }
                />
            </div>

            <div className={styles.results}>
                {searchResults.isFetching && (
                    <div
                        className={classNames(styles.message, styles.loading)}
                        data-testid="results-loading"
                    >
                        <Spinner size="xspx" />
                        Loading...
                    </div>
                )}
                {searchResults.error && (
                    <div
                        className={classNames(styles.message, styles.error)}
                        data-testid="results-error"
                    >
                        Error loading results
                    </div>
                )}

                {!searchResults.isFetching &&
                    searchResults.search.trim() &&
                    searchResults.data?.users.map((result) => (
                        <div
                            key={result.username}
                            className={classNames(
                                "search-result",
                                styles.searchResult,
                            )}
                            onClick={() => {
                                handleUserSelect?.(result.username)
                                addPageAction("DmNewConversationUserSearch", {
                                    actionType: "init",
                                    source: source,
                                    toUser: result.username,
                                })
                            }}
                        >
                            <ProfilePicture
                                user={{
                                    //  TODO: [MSG-413] We currently don't have the user data from the search endpoint so they will appear as default user color
                                    username: result.username,
                                    inFanclub: false,
                                    hasTokens: false,
                                    isMod: false,
                                    tippedRecently: false,
                                    tippedALotRecently: false,
                                    tippedTonsRecently: false,
                                }}
                                size={40}
                            />
                            <span>{result.username}</span>
                        </div>
                    ))}

                {!searchResults.isFetching &&
                    !searchResults.error &&
                    searchResults.search.trim() &&
                    searchResults.data?.users.length === 0 && (
                        <div
                            className={classNames(styles.message, styles.left)}
                            data-testid="results-empty"
                        >
                            {t`No results found`}
                        </div>
                    )}

                {!searchResults.isFetching &&
                    searchResults.search.trim() === "" && (
                        <div className={styles.initialPlaceholderContainer}>
                            <SearchPlaceholder
                                className={styles.searchPlaceholderSvg}
                            />
                            <Typography
                                size={Sizes.mdpx}
                                weight={Weights.medium}
                                className={styles.searchPlaceholderText}
                            >{t`Enter a creator's name to send a message`}</Typography>
                        </div>
                    )}
            </div>
        </Page>
    )
}
